import * as React from "react";
import { ICheckInBookings } from "../types/Booking.types";
import { Spinner } from "office-ui-fabric-react";
import { MessageBar, MessageBarType, PrimaryButton } from "@fluentui/react";

import "../assets/checkin.css";
import { getRoomInfo } from "../api/rooms";
import { IRoom } from "../types/SiteLocation.types";
import { useHistory } from "react-router-dom";

export interface ICheckInProps {
    siteId: string;
    locationId: string;
    roomId: string;
}

export const CheckIn: React.FunctionComponent<ICheckInProps> = (props) => {
    const [loading, setLoading] = React.useState<boolean>(true);
    const [saving, setSaving] = React.useState<boolean>(false);
    const [roomInfo, setRoomInfo] = React.useState<IRoom>();
    const [checkInBookings, setCheckInBookings] = React.useState<ICheckInBookings>();
    const [checkInError, setCheckInError] = React.useState<string>();
    const [checkedIn, setCheckedIn] = React.useState<boolean>();
    const upcomingMeeting = checkInBookings?.UpcomingMeeting;
    const previousMeeting = checkInBookings?.PreviousMeeting;
    const roomDetails = checkInBookings?.RoomDetails;
    const history = useHistory();
    
    const getData = React.useCallback(async () => {
        const ri = await (await getRoomInfo(Number(props.roomId))).json() as IRoom;
        fetch(`/api/bookings/check-in/${props.siteId}/${props.locationId}/${props.roomId}`)
            .then(response => response.json())
            .then((data: ICheckInBookings) => {
                setRoomInfo(ri);
                setCheckInBookings(data);
                setLoading(false);
            });
    }, [props.locationId, props.roomId, props.siteId]);

    React.useEffect(() => {
        getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    if (loading) {
        return (
            <Spinner label="Loading..." />
        );
    }

    if (!roomDetails) {
        return null;
    }

    
    return (
        <div className="checkin-grid">
            <div className="checkin-title-container">
                <h1>Room Booking<br />Check-in</h1>
            </div>
            <div className="checkin-logo-container">
                <a title="Home" href="/" target="_self"><img src="/api/logo" alt="Organisation logo" /></a>
            </div>
            <div className="checkin-content-area">
                {roomInfo !== undefined && (
                    <MessageBar styles={{ root: { marginBottom: 20 } }} messageBarType={MessageBarType.info}>
                        <strong>Room:</strong> {roomInfo.Name}&emsp;
                        <strong>Location:</strong> {roomInfo.LocationName}&emsp;
                        <strong>Site:</strong> {roomInfo.SiteName}
                    </MessageBar>
                )}
                {checkInError !== undefined && (
                    <MessageBar styles={{ root: { marginBottom: 20 } }} messageBarType={MessageBarType.error}>{checkInError}</MessageBar>
                )}
                <div className="bookings">
                    { previousMeeting != null && (
                        <div className="booking">
                            <h3>Previous Meeting:</h3>
                            <p>
                                <strong><em>{previousMeeting.Description}</em></strong>
                            </p>
                            <p>
                                <strong>Site:</strong> {roomDetails.SiteName}<br />
                                <strong>Location:</strong> {roomDetails.LocationName}<br />
                                <strong>Room:</strong> {roomDetails.Name}<br />
                                <strong>Time:</strong> {new Date(previousMeeting.Start).toLocaleTimeString()} -
                                {new Date(previousMeeting.Finish).toLocaleTimeString()}<br />
                            </p>
                        </div>
                    )}
                    { upcomingMeeting != null && (
                        <div className="booking">
                            <h3>Upcoming Meeting:</h3>
                            <p>
                                <strong><em>{upcomingMeeting.Description}</em></strong>
                            </p>
                            <p>
                                <strong>Site:</strong> {roomDetails.SiteName}<br />
                                <strong>Location:</strong> {roomDetails.LocationName}<br />
                                <strong>Room:</strong> {roomDetails.Name}<br />
                                <strong>Time:</strong> {new Date(upcomingMeeting.Start).toLocaleTimeString()} -
                                {new Date(upcomingMeeting.Finish).toLocaleTimeString()}<br />
                            </p>
                            {upcomingMeeting.CheckedInByUserId == null && (
                                <div className="checkin-button-container">
                                    <p>Here for your meeting?</p>
                                    <PrimaryButton disabled={saving} onClick={async () => {
                                        setSaving(true);
                                        await fetch(`/api/bookings/check-in/${upcomingMeeting.Id}`, {
                                            method: 'POST',
                                            headers: {
                                                accept: 'application/json'
                                            },
                                            credentials: 'include'
                                        }).then((r) => {
                                            setSaving(false);
                                            if (r.ok) {
                                                setLoading(true);
                                                getData();
                                            } else {
                                                setCheckInError("An error occurred. Unable to check-in.")
                                            }
                                        })
                                    }}>
                                        {saving === true && <><Spinner />&emsp;</>}
                                        Check-in</PrimaryButton>
                                </div>
                            )}
                            {upcomingMeeting.CheckedInByUserId != null && (
                                <div className="checkin-button-container">
                                    <p>You're Checked-In</p>
                                    <div className="checkin-button-container">
                                        <PrimaryButton iconProps={{ iconName: "Home" }}
                                            text="Home"
                                            onClick={() => {
                                                history.push("/");
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    { previousMeeting == null && upcomingMeeting == null && (
                        <div className="booking">
                            <h3>No Bookings</h3>
                            <p>
                                We do not have a meeting recorded for you to check-in to, but this room may be available.
                            </p>
                            <a className="button" href="/menu">Return to Menu</a>
                        </div>
                    )}
                </div>
            </div>
            <div style={{gridColumn: 3, gridRow: 3}}></div>
        </div>
    );
};
